import { useEffect, useState, type KeyboardEvent } from "react";
import { pogMetricNames, type PogMetricName } from "@CommonTypes/merchflow/pog/pogMetrics";
import { Button, Color, Flex, Input, Select, Text } from "src/elements";
import { SelectOption } from "src/elements/Select/store/types";
import { useTranslation } from "src/i18n";

/**
 * The threshold a merchant has actually asked for. Both halves are needed: a
 * score with no number, or a number with no score, filters nothing.
 */
export interface MetricBelowFilterValue {
  metricFilter?: PogMetricName;
  metricBelow?: number;
}

export const ANY_METRIC_OPTION_VALUE = "__any_metric__";

type MetricSelectValue = PogMetricName | typeof ANY_METRIC_OPTION_VALUE;

const isMetricName = (value: MetricSelectValue): value is PogMetricName =>
  value !== ANY_METRIC_OPTION_VALUE;

/**
 * Only a number between 0 and 100 is a threshold a merchant could mean.
 * Anything else leaves the filter off rather than hiding rows for a number
 * nobody typed on purpose.
 */
export const parseThreshold = (raw: string): number | undefined => {
  const trimmed = raw.trim();
  if (trimmed === "") return undefined;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) return undefined;
  return parsed;
};

export const MetricBelowFilter = ({
  value,
  onChange,
  label,
  width = "180px",
  metricNames = pogMetricNames,
}: {
  value: MetricBelowFilterValue;
  onChange: (value: MetricBelowFilterValue) => void;
  /** Overrides the default wording, e.g. "Show plans scoring below". */
  label?: string;
  width?: string;
  /** The scores on offer, for a list that shows only some of them. */
  metricNames?: readonly PogMetricName[];
}) => {
  const { t } = useTranslation("common");
  // The score picked but not yet applied: only a whole filter ever reaches `onChange`.
  const [draftMetric, setDraftMetric] = useState(value.metricFilter);
  const [draftThreshold, setDraftThreshold] = useState(
    value.metricBelow === undefined ? "" : String(value.metricBelow),
  );

  useEffect(() => {
    setDraftMetric(value.metricFilter);
  }, [value.metricFilter]);

  useEffect(() => {
    setDraftThreshold(value.metricBelow === undefined ? "" : String(value.metricBelow));
  }, [value.metricBelow]);

  const metricOptions: SelectOption<MetricSelectValue>[] = [
    { value: ANY_METRIC_OPTION_VALUE, label: t("metricFilter.anyScore") },
    ...metricNames.map((metricName) => ({
      value: metricName,
      label: t(`planogramMetrics.${metricName}.title`),
    })),
  ];

  const isActive = value.metricFilter !== undefined && value.metricBelow !== undefined;

  const report = (metricFilter: PogMetricName | undefined, metricBelow: number | undefined) => {
    if (metricFilter !== undefined && metricBelow !== undefined) {
      onChange({ metricFilter, metricBelow });
    } else if (isActive) {
      onChange({});
    }
  };

  const apply = () => {
    const metricBelow = parseThreshold(draftThreshold);
    // A number that cannot be a score is not kept as a draft either: showing
    // the merchant what was understood beats a box that quietly does nothing.
    setDraftThreshold(metricBelow === undefined ? "" : String(metricBelow));
    report(draftMetric, metricBelow);
  };

  const changeMetric = (next: MetricSelectValue) => {
    if (!isMetricName(next)) {
      clear();
      return;
    }
    setDraftMetric(next);
    const metricBelow = parseThreshold(draftThreshold);
    if (metricBelow !== undefined) onChange({ metricFilter: next, metricBelow });
  };

  const clear = () => {
    setDraftMetric(undefined);
    setDraftThreshold("");
    onChange({});
  };

  const hasMetric = draftMetric !== undefined;

  return (
    <Flex gap="8px" align="center">
      <Text variant="small2" color={Color.textSecondary} whiteSpace="nowrap">
        {label ?? t("metricFilter.label")}
      </Text>

      <Select
        width={width}
        options={metricOptions}
        value={draftMetric ?? ANY_METRIC_OPTION_VALUE}
        setValue={changeMetric}
      />

      <Input
        width="90px"
        title={t("metricFilter.thresholdTitle")}
        placeholder={t("metricFilter.thresholdPlaceholder")}
        value={draftThreshold}
        setValue={setDraftThreshold}
        isDisabled={!hasMetric}
        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
          if (event.key === "Enter") apply();
        }}
      />

      <Button color="primary" isDisabled={!hasMetric} onClick={apply}>
        {t("metricFilter.apply")}
      </Button>

      {isActive && (
        <Button color="primary" variant="borderOnly" onClick={clear}>
          {t("metricFilter.clear")}
        </Button>
      )}
    </Flex>
  );
};
